"use client";

import { Home, MessageSquare, Send, User, BarChart3, Wallet, Ticket, Settings } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { useAuthStore } from "@/stores/auth-store";
import { hasPermission } from "@/lib/rbac";
import type { UserRole } from "@/lib/rbac";

interface MobileNavItem {
  id: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  badge?: number;
  roles?: UserRole[]; // if set, only these roles see this item
}

const allMobileItems: MobileNavItem[] = [
  { id: "dashboard", labelKey: "nav.dashboard", icon: Home, href: "/dashboard" },
  { id: "inbox", labelKey: "nav.inbox", icon: MessageSquare, href: "/inbox", badge: 142 },
  { id: "campaigns", labelKey: "nav.campaigns", icon: Send, href: "/campaigns" },
  { id: "analytics", labelKey: "nav.analytics", icon: BarChart3, href: "/analytics" },
  { id: "tickets", labelKey: "nav.supportTickets", icon: Ticket, href: "/support-tickets" },
  { id: "billing", labelKey: "nav.billing", icon: Wallet, href: "/billing" },
  { id: "settings", labelKey: "nav.settings", icon: Settings, href: "/settings" },
  { id: "profile", labelKey: "nav.profileSettings", icon: User, href: "/profile-settings" },
];

// Which items to show per role (max 5 for mobile)
function getMobileItemsForRole(role: UserRole): string[] {
  switch (role) {
    case 'owner':
    case 'admin':
      return ['dashboard', 'inbox', 'campaigns', 'analytics', 'profile'];
    case 'marketing':
      return ['dashboard', 'campaigns', 'analytics', 'inbox', 'profile'];
    case 'cs_agent':
      return ['dashboard', 'inbox', 'tickets', 'profile'];
    case 'finance':
      return ['dashboard', 'billing', 'analytics', 'profile'];
    case 'developer':
      return ['dashboard', 'settings', 'profile'];
    default:
      return ['dashboard', 'inbox', 'campaigns', 'analytics', 'profile'];
  }
}

export function MobileNavigation() {
  const pathname = usePathname();
  const t = useTranslations();
  const user = useAuthStore(s => s.user);

  if (!user) return null;

  const visibleIds = getMobileItemsForRole(user.role);
  const navItems = allMobileItems.filter(item => visibleIds.includes(item.id));

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 md:hidden">
      <div className="flex items-center justify-around h-16">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = pathname === item.href || pathname.startsWith(item.href + "/");
          const labelKey = item.labelKey.split('.')[1] || item.labelKey;

          return (
            <Link
              key={item.id}
              href={item.href}
              className="flex flex-col items-center justify-center w-full h-full relative"
            >
              <Icon className={`w-6 h-6 ${isActive ? "text-blue-600" : "text-gray-600"}`} />
              <span className={`text-[10px] mt-1 ${isActive ? "text-blue-600 font-medium" : "text-gray-600"}`}>
                {t(`nav.${labelKey}`)}
              </span>

              {item.badge && (
                <span className="absolute top-1 right-1/4 min-w-[20px] h-5 px-1.5 bg-red-600 text-white text-[10px] font-semibold rounded-full flex items-center justify-center">
                  {item.badge > 99 ? "99+" : item.badge}
                </span>
              )}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
